import { get } from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import LanguageSelector from 'components/LanguageSelector';
import FooterEmailSignUpContainer from 'containers/FooterEmailSignUpContainer';
import Rudderstack from 'utils/Rudderstack';
import Settings from 'utils/Settings';
import { replaceSubdomain, navigateToUrl } from 'utils/Url';

const FooterController = class {
  static renderFooterEmailSignUpForm = ({ store }) => {
    const domElement = document.getElementById('footer-email-sign-up-form-container');
    if (domElement) {
      ReactDOM.render(
        <Provider store={store}>
          <FooterEmailSignUpContainer />
        </Provider>,
        domElement
      );
    }
  };

  static renderLanguageSelector = () => {
    const domElement = document.getElementById('footer-language-selector-container');

    if (domElement) {
      ReactDOM.render(
        <LanguageSelector
          supportedLocales={Settings.supportedLocales}
          currentLocale={Settings.currentLocale}
          onSelectLanguage={FooterController.onSelectLanguage}
        />,
        domElement
      );
    }
  };

  static onSelectLanguage = event => {
    const selectedLocale = get(event, ['target', 'value']);
    if (selectedLocale === Settings.currentLocale) {
      return;
    }

    const redirectURL =
      selectedLocale === Settings.shopLocale
        ? replaceSubdomain(FooterController.windowLocationHref(), '')
        : replaceSubdomain(FooterController.windowLocationHref(), selectedLocale);

    navigateToUrl(redirectURL);
  };

  static addLinkTracking = () => {
    const footerLinkLists = document.querySelectorAll('.Footer__link-group');

    footerLinkLists.forEach(linkList => {
      const footerLinkCategory = linkList.querySelector('.Footer__heading').textContent;
      linkList.addEventListener('click', event => {
        Rudderstack.footerLinkClicked({
          footerLinkCategory,
          footerLinkName: event.target.textContent,
        });
      });
    });
  };

  static addSocialTracking = () => {
    const socialLinks = document.querySelector('.Footer__social-icons-list');
    const socialLinkHeading = document.querySelector('.Footer__social-icons-heading').textContent;

    socialLinks.addEventListener('click', event => {
      Rudderstack.footerLinkClicked({
        footerLinkCategory: socialLinkHeading,
        footerLinkName: event.target.alt,
      });
    });
  };

  static addFlagTracking = () => {
    const flagLinks = document.querySelector('.Footer__country-flags-list');
    const flagLinkHeading = 'Country Flags';

    flagLinks.addEventListener('click', event => {
      Rudderstack.footerLinkClicked({
        footerLinkCategory: flagLinkHeading,
        footerLinkName: event.target.alt,
      });
    });
  };

  static addPrivacyAndTermsTracking = () => {
    const privacyAndTermsLinks = document.querySelector('.Footer__privacy-and-terms');
    const privacyAndTermsHeading = 'Privacy and Terms';

    privacyAndTermsLinks.addEventListener('click', event => {
      Rudderstack.footerLinkClicked({
        footerLinkCategory: privacyAndTermsHeading,
        footerLinkName: event.target.textContent,
      });
    });
  };

  static pollForGladlyScript = () => {
    const chatButton = document.querySelector('.Footer__gladly-chat-button');
    const maxAttempts = 35;
    const interval = 500;

    if (!chatButton) return;

    let attempts = 0;

    const intervalId = setInterval(() => {
      const isGladlyEnabled = window?.Gladly && typeof window.Gladly.show === 'function';
      if (isGladlyEnabled) {
        clearInterval(intervalId);

        chatButton.style.display = 'block';
        chatButton.addEventListener('click', event => {
          event.preventDefault();
          if (isGladlyEnabled) {
            window.Gladly.show();
          }
        });
      } else if (++attempts >= maxAttempts) {
        clearInterval(intervalId);
      }
    }, interval);
  };

  static addEventListeners = () => {
    FooterController.addLinkTracking();
    FooterController.addSocialTracking();
    FooterController.addFlagTracking();
    FooterController.addPrivacyAndTermsTracking();
  };

  static windowLocationHref = () => window.location.href;

  static init = store => {
    FooterController.renderFooterEmailSignUpForm({
      store,
    });
    FooterController.renderLanguageSelector();
    FooterController.pollForGladlyScript();
    FooterController.addEventListeners();
  };
};

export default FooterController;
